import { ReactNode } from "react";
import React from "react";
import { styled } from "../../stitches/stitches.config";
import { heading300, paragraphTextLarge } from "../../stitches/combinedStyles";
import CheckIcon from "./assets/check.inline.svg";
import CrossIcon from "./assets/cross.inline.svg";
import Logo from "./assets/logo.inline.svg";

export interface FeatureProps {
  name: string;
  our: boolean;
  theirs: boolean;
}

interface ComparisonTableProps {
  title: ReactNode;
  ourTitle: ReactNode;
  theirsTitle: ReactNode;
  features: FeatureProps[];
  moreInfo?: ReactNode;
}

const ComparisonTable = ({
  title,
  ourTitle,
  theirsTitle,
  features,
  moreInfo,
}: ComparisonTableProps) => {
  return (
    <Wrapper>
      <Table
        style={{ gridTemplateRows: `repeat(${features.length + 2}, auto)` }}
      >
        <Title>{title}</Title>
        <FieldLeft className="header">{ourTitle}</FieldLeft>
        <FieldRight className="header">{theirsTitle}</FieldRight>
        {features.map((feature, index) => (
          <FeatureWrapper key={index} style={{ gridRow: index + 2 }}>
            <FeatureName>{feature.name}</FeatureName>
            <FieldLeft>
              <IconWrapper>
                {feature.our ? <CheckIcon /> : <CrossIcon />}
              </IconWrapper>
            </FieldLeft>
            <FieldRight>
              <IconWrapper>
                {feature.theirs ? <CheckIcon /> : <CrossIcon />}
              </IconWrapper>
            </FieldRight>
          </FeatureWrapper>
        ))}

        <OurWrapper />
        <InfoWrapper>{moreInfo}</InfoWrapper>
        <FieldLeft className="footer">
          <Logo />
        </FieldLeft>
      </Table>
    </Wrapper>
  );
};

export default ComparisonTable;

const Wrapper = styled("section", {
  backgroundColor: "#F5FAFF",
  borderRadius: 6,
  fontSize: 9,
  padding: 16,

  "@sm": {
    fontSize: 12,
    padding: 32,
  },
  "@md": {
    fontSize: 15,
    margin: "64px 32px",
    padding: 100,
  },
  "@lg": {
    fontSize: 20,
  },
});

const Title = styled("h2", {
  ...heading300,
  fontSize: "2em",
  margin: 0,
  lineHeight: "30px",
  "@md": {
    padding: "16px 8px 8px",
    lineHeight: "52px",
  },
});

const Table = styled("div", {
  margin: "auto",
  width: "100%",
  maxWidth: 770,
  display: "grid",
  rowGap: "6px",
  "@md": {
    rowGap: "16px",
  },

  gridTemplateColumns: "3fr minmax(60px, 1fr) minmax(60px, 1fr)",
});

const FeatureWrapper = styled("div", {
  backgroundColor: "white",
  gridColumn: "1/4",
  display: "grid",
  gridTemplateColumns: "3fr minmax(60px, 1fr) minmax(60px, 1fr)",
  border: "2px solid rgba(26, 26, 26, 0.1)",
  marginLeft: -2,
  marginRight: -2,
  borderRadius: "5px",
});

const FeatureName = styled("div", {
  ...paragraphTextLarge,
  fontSize: "1em",
  lineHeight: 1,
  padding: 8,
  "@sm": {
    padding: 10,
  },
  "@md": {
    padding: 20,
  },
  margin: 0,
  display: "flex",
  alignItems: "center",
});

const baseField = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center" as const,
};

const fieldHeader = {
  gridRow: 1,
  padding: 8,
  paddingTop: 16,
  "@md": {
    paddingTop: 32,
    paddingBottom: 16,
  },
};

const fieldFooter = {
  gridRowEnd: -1,
  paddingTop: 0,
  paddingBottom: 6,

  "@sm": {
    paddingTop: 6,
    paddingBottom: 16,
  },
  "@md": {
    paddingTop: 12,
    paddingBottom: 24,
  },

  "& svg": {
    width: "70%",
  },
};

const FieldLeft = styled("div", {
  ...baseField,
  fontWeight: 600,
  gridColumn: 2,
  zIndex: 10,
  "&.header": {
    ...fieldHeader,
  },
  "&.footer": {
    ...fieldFooter,
  },
});

const FieldRight = styled("div", {
  ...baseField,
  gridColumn: 3,
  "&.header": {
    ...fieldHeader,
  },
  "&.footer": {
    ...fieldFooter,
  },
});

const IconWrapper = styled("div", {
  "& svg": {
    maxWidth: "100%",
    maxHeight: "100%",
  },
  width: 14,
  height: 14,
  "@sm": {
    width: 20,
    height: 20,
  },
  "@md": {
    width: 30,
    height: 30,
  },
  "@lg": {
    width: 40,
    height: 40,
  },
});

const InfoWrapper = styled("div", {
  padding: 6,

  "@md": {
    padding: 16,
  },
});

const OurWrapper = styled("div", {
  gridColumn: "2",
  gridRow: "1/-1",
  backgroundColor: "white",
  boxShadow: "2px 2px 9px 0px #00000040",
  border: "1px solid #000000",
  borderRadius: 8,
});
